import * as React from 'react'
import { Container } from '@amzn/dvui/dist/Container'
import { Link, LinkProps } from '@amzn/dvui/dist/Link'
import { Spinner } from '@amzn/dvui/dist/Spinner'

import { Episode } from '../episode/episode'
import { CollectionPagination } from '../collection/pagination'

import * as st from './episodeList.module.scss'
import * as stUtility from '../utilityClasses.module.scss'
import { CollectionType } from '../collection/collectionTypes'
import { LocalizerProps, TextLocalizer, withLocalization } from '../localizer/localizer'
import { IDetailPageState } from '../../reducers'
import { IRequestContext, sanitizedRequestContextSelector } from '../../selectors/requestContext'
import { requestEpisodes } from '../../utils/wps'
import { connect } from 'react-redux'
import { StringId } from '../localizer/stringId'
import { detailPagePath, logRefTag, MakeUrlArgs } from '../../utils/url'
import { episodesAvailableSelector, episodesSeenSelector, TitleIdToAvailable } from '../../selectors/actionFragment'
import { FIRST_EXPANDER_THRESHOLD, SECOND_EXPANDER_THRESHOLD } from '../../reducers/collections'
import { CloseableDropList } from '../../utils/closeableDropList'

export const EPISODE_START_INDEX = 0
const DROPLIST_ID = 'sorting-droplist'

//TODO(AVWebDP-285) Add IMDB Rating
export enum SortingType {
  EpisodeNumberAscending = 'EpisodeNumberAscending',
  EpisodeNumberDescending = 'EpisodeNumberDescending',
  Available = 'Available',
}

export const TYPE_TO_STRING_ID = {
  EpisodeNumberAscending: StringId.DV_DP_EPISODE_NUM_ASCENDING,
  EpisodeNumberDescending: StringId.DV_DP_EPISODE_NUM_DESCENDING,
  Available: StringId.DV_DP_EPISODE_AVAILABLE,
} as Record<SortingType, StringId>

export const TYPE_TO_REFTAG = {
  EpisodeNumberAscending: 'atv_dp_ep_ena',
  EpisodeNumberDescending: 'atv_dp_ep_end',
  Available: 'atv_dp_ep_av',
} as Record<SortingType, string>

interface SortingFunc {
  (titleIds: string[], titleIdToAvailable: TitleIdToAvailable): string[]
}

/* The Episodes are naturally ordered by Episode number ascending */
export const TYPE_TO_SORT_FUNC = {
  EpisodeNumberAscending: (titleIds: string[]) => titleIds,
  EpisodeNumberDescending: (titleIds: string[]) => [...titleIds].reverse(),
  // Split into available and unavailable
  Available: (titleIds: string[], titleIdToAvailable: TitleIdToAvailable) => {
    const availableTitles = titleIds.filter((titleId) => titleIdToAvailable[titleId])
    const unavailableTitles = titleIds.filter((titleId) => !titleIdToAvailable[titleId])
    return [
      ...availableTitles,
      ...unavailableTitles
    ]
  },
} as Record<SortingType, SortingFunc>

/* Dictates the order of the Sorting Options */
export const TYPE_LIST = [
  SortingType.EpisodeNumberAscending,
  SortingType.EpisodeNumberDescending,
  SortingType.Available
]

export interface OwnProps {
  header?: string
  collectionType: CollectionType
  totalEpisodeSize: number
  cardTitleIds: string[]
}

export interface IStateProps {
  requestContext: IRequestContext
  pageTitleId: string
  isElcano: boolean
  useEpisodeListSortingT1: boolean
  useEpisodeListSortingT2: boolean
  episodesSeen: number
  titleIdToAvailable: TitleIdToAvailable
}

export type EpisodeListProps = OwnProps & IStateProps & LocalizerProps

export interface EpisodeListState {
  episodesHidden: boolean,
  sortingState: SortingType,
  sortingChanged: boolean
}

export const dropdownText = (sortingType: SortingType, sortingChanged: boolean) =>
  sortingChanged ? TYPE_TO_STRING_ID[sortingType] : StringId.DV_DP_EPISODE_SORT_BY

class EpisodeListComp extends React.Component<EpisodeListProps, EpisodeListState> {
  constructor(props: EpisodeListProps) {
    super(props)
    this.state = {
      episodesHidden: props.cardTitleIds.length < props.totalEpisodeSize,
      sortingState: SortingType.EpisodeNumberAscending, // Initially episodes are sorted in ascending episode order,
      sortingChanged: false
    }
  }

  componentDidMount() {
    const { requestContext, pageTitleId, isElcano, useEpisodeListSortingT1, useEpisodeListSortingT2,
            collectionType, totalEpisodeSize, cardTitleIds, episodesSeen } = this.props

    if (collectionType === 'episodes') {
      /* If not all the episodes have been fetched */
      if (cardTitleIds.length < totalEpisodeSize) {
        /* We auto-expand the list if the episodes seen is greater than the second threshold */
        if ((useEpisodeListSortingT1 || useEpisodeListSortingT2) && episodesSeen >= SECOND_EXPANDER_THRESHOLD) {
          requestEpisodes(requestContext.rawContext, pageTitleId, isElcano, SECOND_EXPANDER_THRESHOLD,
            totalEpisodeSize - SECOND_EXPANDER_THRESHOLD)
          this.setState({
            episodesHidden: false
          })
        }
      } else {
        // Hide expander if no episodes have been watched and enough episodes in season
        this.setState({
          episodesHidden: episodesSeen === 0 && cardTitleIds.length > FIRST_EXPANDER_THRESHOLD
        })
      }
    }
  }

  shouldComponentUpdate(nextProps: EpisodeListProps, nextState: EpisodeListState) {
    return nextProps.cardTitleIds !== this.props.cardTitleIds ||
      nextState.sortingState !== this.state.sortingState ||
      nextState.episodesHidden !== this.state.episodesHidden
  }

  onClickHandler = (sortingType?: SortingType) => (e: React.MouseEvent<HTMLElement> | React.MouseEvent<HTMLAnchorElement>) => {
    const { requestContext, pageTitleId, isElcano, totalEpisodeSize, cardTitleIds } = this.props

    e.preventDefault() /* Prevents redirect */

    if (!sortingType || this.state.sortingState !== sortingType) {
      logRefTag(sortingType ? TYPE_TO_REFTAG[sortingType] : 'atv_dp_ep_exp')
    }

    this.setState(prevState => {
      return sortingType && prevState.sortingState === sortingType? prevState : {
        episodesHidden: false,
        sortingState: sortingType || prevState.sortingState,
        sortingChanged: prevState.sortingChanged || sortingType !== undefined
      }
    })
    if (cardTitleIds.length < totalEpisodeSize) {
      requestEpisodes(requestContext.rawContext, pageTitleId, isElcano, SECOND_EXPANDER_THRESHOLD,
        totalEpisodeSize - SECOND_EXPANDER_THRESHOLD)
    }
  }

  render() {
    const { header, cardTitleIds, totalEpisodeSize, collectionType, useEpisodeListSortingT1, useEpisodeListSortingT2,
            pageTitleId, requestContext, titleIdToAvailable, episodesSeen, getString } = this.props

    const { episodesHidden, sortingState, sortingChanged } = this.state

    /* We show the spinner if every episode hasn't been added to the store yet */
    const showSpinner = cardTitleIds.length < totalEpisodeSize

    if (!collectionType) {
      return null
    }

    /* For T2 we sort the episodes by the current sorting state. */
    const titleIds = collectionType === 'episodes' && !showSpinner && useEpisodeListSortingT2 ?
      TYPE_TO_SORT_FUNC[sortingState](cardTitleIds, titleIdToAvailable) : cardTitleIds

    const cardComponents =
      titleIds.map((titleId, idx) => {
        const episodeProps = {
          titleId,
          collectionType,
          epListIndex: `${collectionType}-${idx}`,
          isFirstEpisode: idx === 0,
          isBonus: collectionType === "bonus"
        }
        return (
          <React.Fragment key={`episode-${titleId}-${idx}`}>
            <Episode {...episodeProps} />
          </React.Fragment>
        )
      })

    if ((cardComponents || []).length === 0) {
      return null
    }

    const paginationArgs = {
      totalEpisodeSize,
      collectionType,
    }

    const dropListLinks = TYPE_LIST.map((sortingType) => {
      const isSelected = sortingType === sortingState
      const stringId = TYPE_TO_STRING_ID[sortingType]

      return {
        id: sortingType,
        name: sortingType,
        text:
          <span className={isSelected? st.boldText : ''}>
            <TextLocalizer stringID={stringId} />
          </span>,
        isHighlighted: isSelected,
        onClick: this.onClickHandler(sortingType),
        ariaLabel: this.props.getString(stringId)
      } as LinkProps
    })

    /* Url for Core Experience */
    const params = {
      get: {
        "episodeListSize": `${totalEpisodeSize}`
      }
    } as MakeUrlArgs
    const pageUrl = requestContext.makeUrl(detailPagePath(pageTitleId), params)

    /* If no episodes have been watched then we show the first 4 episodes + the expander
    *  Otherwise if < 25 have been watched we show the first 25 episodes + the expander
    *  Else we show all the episodes (the list auto-expands) */
    const threshold = episodesSeen === 0 ? FIRST_EXPANDER_THRESHOLD : SECOND_EXPANDER_THRESHOLD

    /* TREATMENT 1 & 2 */
    if ((useEpisodeListSortingT1 || useEpisodeListSortingT2) && collectionType === 'episodes') {
      return (
        <Container className={stUtility.pageSection}>
          <div className={st.headerWithDropList}>
            { header &&
            <h1 className={st.episodeListHeader}>{header} ({totalEpisodeSize})</h1> }
            { useEpisodeListSortingT2 && <div className={st.dropListContainer}>
              <CloseableDropList links={dropListLinks} customDropListID={DROPLIST_ID} isCustomTrigger={true}
                                 right={true} customText={getString(dropdownText(sortingState, sortingChanged))}/>
            </div> }
          </div>
          <ol className={st.episodeList}>
            {cardComponents.slice(EPISODE_START_INDEX, threshold)}
            <span className={episodesHidden? st.hidden : ''}>
              {showSpinner?
                <div className={st.episodeSpinner}><Spinner /></div> :
                cardComponents.slice(threshold)
              }
            </span>
          </ol>
          { episodesHidden &&
          <Link className={st.expanderLink} href={pageUrl} onClick={this.onClickHandler()} data-automation-id={'ep-expander'}
                data-testid={'ep-expander'} ariaLabel={getString(StringId.DV_DP_EPISODE_EXPANDER, { episodeCount: `${totalEpisodeSize}` })}>
              <TextLocalizer stringID={StringId.DV_DP_EPISODE_EXPANDER} replace={{ episodeCount: `${totalEpisodeSize}` }} />
          </Link> }
        </Container>
      )
    }

    /* CONTROL */
    return (
      <Container className={stUtility.pageSection}>
        { header &&
        <h1 className={st.episodeListHeader}>{header} ({totalEpisodeSize})</h1>}
        <ol className={st.episodeList}>{cardComponents}</ol>
        <CollectionPagination {...paginationArgs} />
      </Container>
    )
  }
}

const mapStateToProps = (state: IDetailPageState): IStateProps => ({
  requestContext: sanitizedRequestContextSelector(state),
  pageTitleId: state.pageTitleId,
  isElcano: Boolean(state.features.isElcano),
  useEpisodeListSortingT1: Boolean(state.features.useEpisodeListSortingT1),
  useEpisodeListSortingT2: Boolean(state.features.useEpisodeListSortingT2),
  episodesSeen: episodesSeenSelector(state),
  titleIdToAvailable: episodesAvailableSelector(state)
})

export const EpisodeList = withLocalization(connect(mapStateToProps)(EpisodeListComp))

